(function (root, factory) {
  var api = factory(root);

  root.PjDesignLabRenderer = api;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var ZOOM_DEFAULT = 1;
  // The client's accessory artboards are 3" x 3"; petBox values are inches on one.
  var ARTBOARD_INCHES = 3;
  var ZOOM_MIN = 0.4;
  var ZOOM_MAX = 4;
  var ZOOM_STEP = 0.25;

  function clampPercent(value) {
    var percent = Number(value);
    if (!Number.isFinite(percent)) return 0;
    return Math.min(100, Math.max(0, percent));
  }

  function clampZoom(value) {
    var scale = Number(value);
    if (!Number.isFinite(scale)) return ZOOM_DEFAULT;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
  }

  // `=` is the unshifted plus key on a US keyboard; numpad reports `+` / `-`
  // or the older `Add` / `Subtract` keys depending on the browser.
  function zoomDeltaFromKey(event) {
    if (!event || event.ctrlKey || event.metaKey || event.altKey) return 0;

    var key = event.key;
    if (key === '+' || key === '=' || key === 'Add') return ZOOM_STEP;
    if (key === '-' || key === '_' || key === 'Subtract') return -ZOOM_STEP;

    var code = event.code;
    if (code === 'NumpadAdd') return ZOOM_STEP;
    if (code === 'NumpadSubtract') return -ZOOM_STEP;
    return 0;
  }

  function isEditableTarget(target) {
    if (!target) return false;
    var tag = String(target.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return Boolean(target.isContentEditable);
  }

  function normalizeMediaId(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var parts = raw.split('/');
    return parts[parts.length - 1] || raw;
  }

  function imageSource(image) {
    if (!image) return '';
    return (
      image.getAttribute('data-full-src') ||
      image.currentSrc ||
      image.getAttribute('src') ||
      image.src ||
      ''
    );
  }

  function resolveProductImage(doc, mediaId) {
    var targetId = normalizeMediaId(mediaId);
    if (!targetId || !doc || typeof doc.querySelectorAll !== 'function') return '';

    var media = doc.querySelectorAll('[data-media-id]');
    for (var index = 0; index < media.length; index += 1) {
      if (normalizeMediaId(media[index].getAttribute('data-media-id')) !== targetId) continue;
      var image =
        media[index].tagName === 'IMG'
          ? media[index]
          : media[index].querySelector && media[index].querySelector('img');
      var source = imageSource(image);
      if (source) return source;
    }

    return '';
  }

  // The base image comes from the template's media key only. Alt text and
  // data-title cannot tell a flat lay apart from a model shot, and a pet must
  // never land on model imagery, so an unconfigured color reports itself here
  // instead of guessing at a gallery slide.
  function listGalleryMedia(doc) {
    if (!doc || typeof doc.querySelectorAll !== 'function') return [];

    var nodes = doc.querySelectorAll('[data-media-id]');
    var seen = {};
    var media = [];

    for (var index = 0; index < nodes.length; index += 1) {
      var node = nodes[index];
      var mediaId = normalizeMediaId(node.getAttribute('data-media-id'));
      if (!mediaId || seen[mediaId]) continue;

      var image = node.tagName === 'IMG' ? node : node.querySelector && node.querySelector('img');
      seen[mediaId] = true;
      media.push({
        mediaId: mediaId,
        title:
          node.getAttribute('data-title') ||
          (image && typeof image.getAttribute === 'function' ? image.getAttribute('alt') : '') ||
          '',
      });
    }

    return media;
  }

  function selectedBackground(doc) {
    if (!doc || typeof doc.querySelector !== 'function') return '';
    var selected =
      doc.querySelector('input[name*="-Color-"]:checked') ||
      doc.querySelector('input[name*="-Background-"]:checked') ||
      doc.querySelector('input[name*="-Colour-"]:checked') ||
      doc.querySelector('input[name*="Color"]:checked');
    if (selected && selected.value) return String(selected.value).trim();

    var background = doc.querySelector(
      '.backgroundHiddenInput:not(.backgroundHiddenInputBandana)'
    );
    return background && background.value ? String(background.value).trim() : '';
  }

  // Garment configuration (Long Sleeve Set, Pants Only, ...). Read live from the
  // PDP form because the shopper can switch it while the studio is open.
  function selectedStyle(doc) {
    if (!doc || typeof doc.querySelector !== 'function') return '';
    var select =
      doc.querySelector('#surge-combined-listing-shell .native-style-select') ||
      doc.querySelector('.native-style-select');
    if (!select || (select.classList && select.classList.contains('placeholder-active'))) {
      return '';
    }
    return select.value ? String(select.value).trim() : '';
  }

  function productColors(doc) {
    if (!doc || typeof doc.getElementById !== 'function') return [];

    var node = doc.getElementById('pjDesignLabPlacementRequiredColors');
    if (!node) return [];

    try {
      var parsed = JSON.parse(node.textContent || node.innerHTML || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  // Colors reach us three ways: chosen by the shopper, implied because the
  // product only sells one color, or not at all on a color-split product where
  // the lone template owns the page. Only a real multi-color product with
  // nothing chosen yet gets the ticket's Pink Dream default.
  function resolveColor(doc, contextColor) {
    var chosen = contextColor == null ? '' : String(contextColor).trim();
    if (chosen) return chosen;

    var colors = productColors(doc).filter(function (color) {
      return color != null && String(color).trim();
    });
    if (colors.length === 1) return String(colors[0]).trim();
    if (colors.length > 1) return soleTemplateColor(doc, colors) || 'Pink Dream';

    return '';
  }

  function soleTemplateColor(doc, colors) {
    if (!doc || typeof doc.getElementById !== 'function') return '';
    var node = doc.getElementById('pjDesignLabPlacementConfig');
    if (!node) return '';

    var catalog;
    try {
      catalog = JSON.parse(node.textContent || node.innerHTML || 'null');
    } catch (error) {
      return '';
    }
    var templates = catalog && Array.isArray(catalog.templates) ? catalog.templates : [];
    var placementApi = root.PjDesignLabPlacement;
    var keyOf = function (value) {
      return placementApi && typeof placementApi.normalizeColorKey === 'function'
        ? placementApi.normalizeColorKey(value)
        : String(value || '').trim().toLowerCase();
    };

    var color = '';
    var key = '';
    for (var index = 0; index < templates.length; index += 1) {
      var template = templates[index];
      var templateColor = template && String(template.color || template.colorKey || '').trim();
      if (!templateColor) continue;
      var templateKey = keyOf(templateColor);
      if (key && templateKey !== key) return '';
      if (!key) {
        key = templateKey;
        color = templateColor;
      }
    }
    if (!key) return '';

    var offered = colors.some(function (candidate) {
      return keyOf(candidate) === key;
    });
    return offered ? color : '';
  }

  function buildComposition(placement, context) {
    var petImages = context && Array.isArray(context.petImages) ? context.petImages : [];
    if (!placement || !placement.ok) {
      return { ok: false, reason: (placement && placement.reason) || 'missing-template' };
    }
    if (!placement.imageUrl && !placement.mediaId) {
      return { ok: false, reason: 'missing-flat-lay' };
    }
    if (petImages.length < placement.petCount || petImages.slice(0, placement.petCount).some(function (url) {
      return !url;
    })) {
      return { ok: false, reason: 'missing-pet-image' };
    }

    var settings = context && Array.isArray(context.petSettings) ? context.petSettings : [];
    return {
      ok: true,
      color: placement.color,
      colorKey: placement.colorKey,
      mediaId: placement.mediaId,
      imageUrl: placement.imageUrl,
      pets: placement.slots.map(function (slot, index) {
        var petSetting = settings[index] || {};
        return {
          imageUrl: petSetting.processedImage || petImages[index],
          slot: slot,
          active: index === Math.max(0, Number(context && context.activePet) || 0),
          processed: Boolean(petSetting.processedImage),
          // A failed pet shows its untouched upload rather than the previous
          // mode's output, so no stale cutout is passed off as the new mode.
          processingFailed: Boolean(petSetting.processingError),
          extractionMode: petSetting.extractionMode || 'background',
          style: petSetting.style || '',
          accessory: petSetting.accessory || '',
          accessoryOverlay: petSetting.accessoryOverlay || null,
          accessoryAdjust: petSetting.accessoryAdjust || null,
          scale: Number(petSetting.scale) > 0 ? Number(petSetting.scale) : 1,
          offsetX: Number(petSetting.offsetX) || 0,
          offsetY: Number(petSetting.offsetY) || 0,
          eraseMaskUrl: petSetting.eraseMaskUrl || '',
        };
      }),
    };
  }

  // ---- Accessory artboards -------------------------------------------------
  // An accessory published with a `petBox` is a square artboard: its artwork
  // is drawn whole and untouched over the pet's slot, and the pet is fitted
  // into the box on it - scaled proportionally to the largest size that fits,
  // centred on the artboard horizontally, and either top-aligned to the box or
  // centred in it (`anchor`). The fit uses the pet's visible pixels, not the
  // image edges: the style filters return a padded 1024px square, and padding
  // would otherwise shrink and lower the pet against the accessory.

  var FULL_BOUNDS = { x: 0, y: 0, width: 1, height: 1 };
  var ALPHA_VISIBLE = 16;

  // Visible-pixel box of RGBA pixel data, as fractions of the image. An image
  // with nothing visible counts as all visible, so it still draws.
  function alphaBounds(data, width, height) {
    var minX = width;
    var minY = height;
    var maxX = -1;
    var maxY = -1;
    for (var y = 0; y < height; y += 1) {
      for (var x = 0; x < width; x += 1) {
        if (data[(y * width + x) * 4 + 3] <= ALPHA_VISIBLE) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return FULL_BOUNDS;
    return {
      x: minX / width,
      y: minY / height,
      width: (maxX - minX + 1) / width,
      height: (maxY - minY + 1) / height,
    };
  }

  // Where the whole pet image sits on the artboard, in fractions of the
  // artboard, so that its visible `bounds` land in the box.
  function fitPetToArtboard(petBox, imageWidth, imageHeight, bounds) {
    var visible = bounds || FULL_BOUNDS;
    var boxY = petBox.y / ARTBOARD_INCHES;
    var boxW = petBox.width / ARTBOARD_INCHES;
    var boxH = petBox.height / ARTBOARD_INCHES;
    var visibleW = visible.width * imageWidth;
    var visibleH = visible.height * imageHeight;

    // One factor for both axes, so the pet is never stretched. `fit: height`
    // fills the box top to bottom for accessories that touch both ends of the
    // pet (the elf's hat and collar), letting a wide face run past the box's
    // sides but never off the artboard.
    var scale =
      petBox.fit === 'height'
        ? Math.min(boxH / visibleH, 1 / visibleW)
        : Math.min(boxW / visibleW, boxH / visibleH);
    var width = imageWidth * scale;
    var height = imageHeight * scale;
    var visibleTop =
      petBox.anchor === 'center' ? boxY + (boxH - visibleH * scale) / 2 : boxY;

    // Centred on the artboard, not the box: the sheet's Santa box sits
    // 0.0084" right of centre, and the spec is centre = 1.5" on every board.
    return {
      left: 0.5 - (visible.x + visible.width / 2) * width,
      top: visibleTop - visible.y * height,
      width: width,
      height: height,
    };
  }

  function measureImage(doc, image) {
    var width = image.naturalWidth || image.width;
    var height = image.naturalHeight || image.height;
    if (!width || !height) return { width: 1, height: 1, bounds: FULL_BOUNDS };

    // Full resolution: a downscaled scan averages faint edges (whiskers, the
    // airbrush haze) below the threshold and shifts the fit.
    var bounds = FULL_BOUNDS;
    try {
      var canvas = doc.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0);
      bounds = alphaBounds(ctx.getImageData(0, 0, width, height).data, width, height);
    } catch (error) {
      // A tainted canvas can't be read; fit the whole image instead.
    }
    return { width: width, height: height, bounds: bounds };
  }

  function createPreviewRenderer(doc, options) {
    var config = options || {};
    var context = {};
    var zoomScale = ZOOM_DEFAULT;
    var initialized = false;

    function region(name) {
      var modal = doc.getElementById('pvModal');
      return modal && modal.querySelector('[data-pv-region="' + name + '"]');
    }

    function showError(reason) {
      var loaded = region('loaded');
      var error = region('error');
      // Every suppression reason is logged, not just the template ones: a blank
      // canvas with no console trail is undiagnosable from a QA report.
      if (root.console && typeof root.console.warn === 'function') {
        root.console.warn('[PJ Design Lab] Preview unavailable.', {
          reason: reason,
          petCount: context.petCount,
          petImages: context.petImages,
          petSettings: context.petSettings,
        });
      }
      if (loaded) loaded.hidden = true;
      if (!error) return false;

      error.hidden = false;
      error.innerHTML = '';

      var panel = doc.createElement('div');
      panel.className = 'pv-render-error';

      var title = doc.createElement('strong');
      title.textContent = 'Preview unavailable';

      var message = doc.createElement('p');
      message.textContent =
        reason === 'missing-template' || reason === 'missing-slots'
          ? 'This pajama color or style is not configured for preview yet.'
          : 'We could not load the product preview. Your uploaded photos are still safe.';

      panel.appendChild(title);
      panel.appendChild(message);
      error.appendChild(panel);
      return false;
    }

    function compositeElement() {
      var loaded = region('loaded');
      if (!loaded) return null;
      if (typeof loaded.querySelector === 'function') {
        return loaded.querySelector('.pv-composite');
      }
      return Array.prototype.find.call(loaded.children || [], function (child) {
        return child.className === 'pv-composite';
      });
    }

    // Off unless the shop metafield turns it on (see pj-design-lab-preview-modal.liquid).
    function watermarkEnabled() {
      var modal = doc.getElementById('pvModal');
      return Boolean(modal && modal.dataset && modal.dataset.watermarkEnabled === 'true');
    }

    // Display only: laid over the composite as its own layer, never drawn into
    // the pet images, which are the same files that go to checkout and print.
    function createWatermark() {
      var mark = doc.createElement('div');
      mark.className = 'pv-composite-watermark';
      mark.setAttribute('aria-hidden', 'true');
      return mark;
    }

    // Right-click "Save image" and dragging an image out both reach the clean
    // pet file beneath the mark, so neither is offered while it is on.
    function onSaveAttempt(event) {
      if (!watermarkEnabled()) return;
      var target = event && event.target;
      if (!target || typeof target.closest !== 'function' || !target.closest('.pv-composite')) {
        return;
      }
      event.preventDefault();
    }

    function applyZoom() {
      var frame = compositeElement();
      if (frame) frame.style.transform = 'scale(' + zoomScale + ')';
      return zoomScale;
    }

    function setZoom(nextScale) {
      zoomScale = clampZoom(nextScale);
      return applyZoom();
    }

    function zoomIn() {
      return setZoom(zoomScale + ZOOM_STEP);
    }

    function zoomOut() {
      return setZoom(zoomScale - ZOOM_STEP);
    }

    function resetZoom() {
      return setZoom(ZOOM_DEFAULT);
    }

    function createPet(pet, index) {
      var image = doc.createElement('img');
      image.className = 'pv-composite-pet';
      if (pet.extractionMode === 'background') image.classList.add('mode-background');
      if (pet.processingFailed) image.classList.add('is-unprocessed');
      if (pet.active) image.classList.add('sel-ring');
      image.src = pet.imageUrl;
      image.alt = 'Uploaded pet ' + (index + 1);
      image.dataset.petIndex = String(index);
      // Reposition is clamped to the slot's own size so a shopper can't drag
      // a pet fully off the flat-lay it's meant to sit on.
      image.style.left = clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) + '%';
      image.style.top = clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) + '%';
      image.style.width = pet.slot.width * pet.scale + '%';
      image.style.transform =
        'translate(-50%, -50%) rotate(' + (Number(pet.slot.rotation) || 0) + 'deg)';

      // Erase/restore edits are a "keep" stencil (opaque = keep, transparent
      // = erased) applied over this pet's own photo via CSS mask - never
      // swapped in as the image itself - so each pet keeps its own picture
      // even when "apply this look to all pets" shares the same stencil.
      //
      // mask-mode is forced to "alpha" rather than left at its "match-source"
      // default: WebKit (Safari, and therefore every browser on iOS) has long
      // defaulted a raster mask-image to *luminance* mode, under which an
      // opaque-but-dark stencil pixel reads as "hide" instead of "keep". The
      // stencil itself is painted white for "keep" (see pj-design-lab-review.js)
      // as a second layer of defense, but the mode is pinned explicitly too so
      // this never again depends on a browser's unstated default.
      if (pet.eraseMaskUrl) {
        var maskValue = 'url("' + pet.eraseMaskUrl + '")';
        image.style.maskImage = maskValue;
        image.style.webkitMaskImage = maskValue;
        image.style.maskMode = 'alpha';
        image.style.webkitMaskMode = 'alpha';
        image.style.maskSize = '100% 100%';
        image.style.webkitMaskSize = '100% 100%';
        image.style.maskRepeat = 'no-repeat';
        image.style.webkitMaskRepeat = 'no-repeat';
      }
      return image;
    }

    // The accessory is a sibling of the pet's image rather than a wrapper
    // around it, so the review editor keeps finding .pv-composite-pet exactly
    // where it did. .pv-composite-pet is aspect-ratio: 1, so a pet's box is
    // square and the catalog's placement percentages - both measured against
    // that box, as the wireframe measures them - transfer directly.
    //
    // The vertical offset is expressed as a share of the accessory's own
    // height: a `top` percentage resolves against the composite's height,
    // while the pet's box is sized off the composite's width, so the two are
    // not interchangeable. Ordering the transform as centre -> rotate -> lift
    // also keeps the accessory square to a rotated slot.
    function createAccessory(pet, index, asset, isBack) {
      var overlay = pet.accessoryOverlay;
      // Both the artwork and its geometry come from the catalog, and the
      // controls layer only hands over an overlay once it has both - there is
      // no geometry to invent here, so an incomplete one simply draws nothing.
      var placement = overlay && overlay.placement;
      if (!overlay || !asset || !placement) return null;

      var width = Number(placement.width);
      var bottom = Number(placement.bottom);
      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(bottom)) return null;

      // Distance from the pet's centre up to the accessory's bottom edge,
      // restated as a percentage of the accessory's own height.
      var lift = 50 + ((bottom - 50) / width) * 100;

      var image = doc.createElement('img');
      image.className = isBack ? 'pv-acc-overlay pv-acc-overlay--back' : 'pv-acc-overlay';
      image.src = asset;
      image.alt = '';
      image.dataset.petIndex = String(index);
      image.style.left = clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) + '%';
      image.style.top = clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) + '%';
      image.style.width = pet.slot.width * pet.scale * (width / 100) + '%';
      image.style.transform =
        'translate(-50%, -50%) rotate(' +
        (Number(pet.slot.rotation) || 0) +
        'deg) translateY(-' +
        lift +
        '%)';
      return image;
    }

    // Pet image sizes and visible-pixel bounds by URL. A pet's first render
    // waits (hidden) on them and redraws once they land, so the pet never
    // flashes at the wrong size.
    var imageSizes = {};

    function petImageSize(url) {
      var cached = imageSizes[url];
      if (cached) return cached === 'pending' ? null : cached;

      imageSizes[url] = 'pending';
      Promise.resolve()
        .then(function () {
          return loadCompositeImage(url);
        })
        .then(function (image) {
          return measureImage(doc, image);
        })
        .catch(function () {
          return { width: 1, height: 1, bounds: FULL_BOUNDS };
        })
        .then(function (measured) {
          imageSizes[url] = measured;
          render();
        });
      return null;
    }

    // Artboard layers cover the pet's whole slot at the artwork's own size,
    // so the accessory never moves or scales against the pet.
    function createArtboardLayer(pet, index, asset, isBack) {
      if (!asset) return null;
      var image = doc.createElement('img');
      image.className = isBack ? 'pv-acc-overlay pv-acc-overlay--back' : 'pv-acc-overlay';
      image.src = asset;
      image.alt = '';
      image.dataset.petIndex = String(index);
      image.style.left = clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) + '%';
      image.style.top = clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) + '%';
      image.style.width = pet.slot.width * pet.scale + '%';
      image.style.transform =
        'translate(-50%, -50%) rotate(' +
        (Number(pet.slot.rotation) || 0) +
        'deg)' +
        accessoryAdjustTransform(layerAdjust(pet, isBack));
      return image;
    }

    // TEST ONLY: one layer's manual placement - the elf's hat (front) and
    // collar (back) are moved separately.
    function layerAdjust(pet, isBack) {
      var adjust = pet && pet.accessoryAdjust;
      return (adjust && adjust[isBack ? 'back' : 'front']) || null;
    }

    // TEST ONLY: the shopper's manual accessory placement, in the artboard's
    // own frame. Percent translate is of the layer, which is the artboard.
    function accessoryAdjustTransform(adjust) {
      if (!adjust) return '';
      return (
        ' translate(' +
        adjust.x * 100 +
        '%, ' +
        adjust.y * 100 +
        '%) rotate(' +
        adjust.rotation +
        'deg) scale(' +
        adjust.scale +
        ')'
      );
    }

    // Canvas twin of accessoryAdjustTransform, for an accessory filling a
    // `size` square centred on the current origin.
    function drawAdjustedAccessory(ctx, image, size, adjust) {
      ctx.save();
      if (adjust) {
        ctx.translate(adjust.x * size, adjust.y * size);
        ctx.rotate((adjust.rotation * Math.PI) / 180);
        ctx.scale(adjust.scale, adjust.scale);
      }
      ctx.drawImage(image, -size / 2, -size / 2, size, size);
      ctx.restore();
    }

    // Places a node by a rect in artboard fractions, offset from the
    // artboard's centre in the slot's rotated frame - the same
    // centre -> rotate -> shift order createAccessory() uses.
    function placeOnArtboard(node, pet, rect) {
      var shiftX = ((rect.left + rect.width / 2 - 0.5) / rect.width) * 100;
      var shiftY = ((rect.top + rect.height / 2 - 0.5) / rect.height) * 100;
      node.style.width = pet.slot.width * pet.scale * rect.width + '%';
      node.style.transform =
        'translate(-50%, -50%) rotate(' +
        (Number(pet.slot.rotation) || 0) +
        'deg) translate(' +
        shiftX +
        '%, ' +
        shiftY +
        '%)';
    }

    function appendArtboard(layer, pet, index) {
      var accessory = pet.accessoryOverlay;
      var petBox = accessory.petBox;
      var measured = petImageSize(pet.imageUrl);
      var back = createArtboardLayer(pet, index, accessory.backAsset, true);
      // The pet stays the whole image at its own aspect, so the erase stencil
      // still maps 1:1.
      var image = createPet(pet, index);
      image.classList.add('pv-composite-pet--artboard');
      if (measured) {
        placeOnArtboard(
          image,
          pet,
          fitPetToArtboard(petBox, measured.width, measured.height, measured.bounds)
        );
      }
      var front = createArtboardLayer(pet, index, accessory.asset, false);

      // The active pet's ring is the sheet's pet box, a thin frame on its own
      // layer rather than a shadow on the pet.
      var ring = null;
      if (pet.active) {
        image.classList.remove('sel-ring');
        ring = doc.createElement('div');
        ring.className = 'pv-composite-ring';
        ring.style.left = image.style.left;
        ring.style.top = image.style.top;
        ring.style.aspectRatio = petBox.width + ' / ' + petBox.height;
        placeOnArtboard(ring, pet, {
          left: petBox.x / ARTBOARD_INCHES,
          top: petBox.y / ARTBOARD_INCHES,
          width: petBox.width / ARTBOARD_INCHES,
          height: petBox.height / ARTBOARD_INCHES,
        });
      }

      [back, image, front, ring].forEach(function (node) {
        if (!node) return;
        if (!measured) node.style.visibility = 'hidden';
        layer.appendChild(node);
      });
    }

    // Missing template degrades to default slots. The launcher passes no
    // mediaId, so with no template nothing names the base photo; use the slide
    // the shopper is looking at (else the first one) rather than suppressing.
    function getSlots(api, catalog, query) {
      var result = (api.getSlotsOrDefault || api.getSlots)(catalog, query);
      if (result && result.fallback && !result.mediaId && !result.imageUrl) {
        var active = doc.querySelector && doc.querySelector('.is-active[data-media-id]');
        var first = (config.listGalleryMedia || listGalleryMedia)(doc)[0];
        result = Object.assign({}, result, {
          mediaId:
            (active && normalizeMediaId(active.getAttribute('data-media-id'))) ||
            (first && first.mediaId) ||
            '',
        });
      }
      return result;
    }

    function placementQuery(color) {
      return {
        color: color,
        style: (config.selectedStyle || selectedStyle)(doc),
        mediaId: context.mediaId,
        petCount: context.petCount,
      };
    }

    function render(nextContext) {
      if (nextContext) context = Object.assign({}, context, nextContext);

      var placementApi = config.placement || root.PjDesignLabPlacement;
      var loadedPlacement = placementApi && placementApi.current && placementApi.current();
      var color = (config.resolveColor || resolveColor)(doc, context.color);
      var query = placementQuery(color);
      var placement =
        loadedPlacement && getSlots(placementApi, loadedPlacement.catalog, query);
      if (
        !placement ||
        placement.fallback ||
        (!placement.ok &&
          ['missing-catalog', 'missing-template', 'missing-slots'].indexOf(placement.reason) >= 0)
      ) {
        if (root.console && typeof root.console.warn === 'function') {
          root.console.warn(
            '[PJ Design Lab] Preview suppressed: no usable placement template.',
            {
              reason: (placement && placement.reason) || 'missing-template',
              requestedColor: color || '(no color axis on this product)',
              requestedColorKey:
                placementApi && placementApi.normalizeColorKey
                  ? placementApi.normalizeColorKey(color)
                  : color,
              requestedStyle: query.style,
              requestedStyleKey:
                placementApi && placementApi.normalizeStyleKey
                  ? placementApi.normalizeStyleKey(query.style)
                  : query.style,
              requestedPetCount: context.petCount,
              placement: placement,
              galleryMedia: (config.listGalleryMedia || listGalleryMedia)(doc),
              diagnostics:
                placementApi && placementApi.diagnose ? placementApi.diagnose(doc) : null,
              hint: 'Compare requestedColorKey / requestedStyleKey against templates[].colorKey / styleKey, and requestedPetCount against templates[].petCountsConfigured.',
            }
          );
        }
      }
      var composition = buildComposition(placement, context);
      if (!composition.ok) return showError(composition.reason);

      var flatLay =
        composition.imageUrl ||
        (config.resolveProductImage || resolveProductImage)(doc, composition.mediaId);
      if (!flatLay) return showError('missing-flat-lay');

      var loaded = region('loaded');
      var error = region('error');
      if (!loaded) return false;

      loaded.innerHTML = '';
      loaded.hidden = false;
      if (error) error.hidden = true;

      var frame = doc.createElement('div');
      frame.className = 'pv-composite';
      frame.dataset.color = composition.colorKey;
      frame.dataset.style = query.style;
      frame.dataset.mediaId = composition.mediaId;
      frame.style.transform = 'scale(' + zoomScale + ')';

      var base = doc.createElement('img');
      base.className = 'pv-composite-base';
      base.src = flatLay;
      base.alt = composition.color + ' pajamas preview';
      frame.appendChild(base);

      var overlay = doc.createElement('div');
      overlay.className = 'pv-composite-overlay';
      composition.pets.forEach(function (pet, index) {
        // An accessory that wraps behind the pet is drawn as a second layer
        // under it, so the pet hides whatever of that layer it overlaps and
        // the artwork reads as worn rather than stamped on top. Order is
        // back layer -> pet -> front layer.
        var accessory = pet.accessoryOverlay;
        if (accessory && accessory.petBox) return appendArtboard(overlay, pet, index);
        var back = accessory && createAccessory(pet, index, accessory.backAsset, true);
        if (back) overlay.appendChild(back);
        overlay.appendChild(createPet(pet, index));
        var front = accessory && createAccessory(pet, index, accessory.asset, false);
        if (front) overlay.appendChild(front);
      });
      frame.appendChild(overlay);
      if (watermarkEnabled()) {
        frame.dataset.watermarked = 'true';
        frame.appendChild(createWatermark());
      }
      loaded.appendChild(frame);

      // The composite is torn down and rebuilt from scratch on every render,
      // so anything overlaid on a pet image (the review editor's drag/erase
      // surface) needs to know to re-attach itself to the new element.
      if (typeof doc.dispatchEvent === 'function' && typeof root.CustomEvent === 'function') {
        doc.dispatchEvent(
          new root.CustomEvent('pj-design-lab:composite-rendered', {
            detail: { activePet: Math.max(0, Number(context.activePet) || 0) },
          })
        );
      }
      return true;
    }

    // --- Flat-lay composite export (design-persistence) ---------------------
    // The live preview above is a stack of positioned/rotated <img> elements;
    // this bakes the same geometry into a single raster image for the cart
    // thumbnail, since Shopify line-item properties need a URL, not a DOM
    // tree. Every formula here is the pixel-space twin of createPet()'s and
    // createAccessory()'s CSS: same slot/offset/scale/rotation percentages,
    // same square (aspect-ratio: 1) pet/accessory boxes, same "cover" fit,
    // same erase-mask destination-in bake, same rotated-local-frame lift for
    // the accessory. Never throws - a failed export just means no thumbnail,
    // not a broken Add to Cart.

    // Fetches the bytes directly and loads the image from a same-origin blob
    // URL, rather than setting crossOrigin='anonymous' on an <img src="...">.
    // The pet/accessory images here are frequently already displayed
    // elsewhere on the page (the live Design Lab preview itself) via a plain
    // <img> with no crossOrigin attribute; browsers cache a resource by its
    // CORS mode along with its URL, so a later crossOrigin='anonymous'
    // request for the same URL can silently fail against that already-cached
    // opaque response. A blob URL sidesteps this entirely - no cross-origin
    // request happens at Image-decode time at all.
    function loadCompositeImage(src) {
      if (!src) return Promise.reject(new Error('no image url'));
      return root
        .fetch(src)
        .then(function (response) {
          if (!response.ok) throw new Error('image fetch failed: ' + src);
          return response.blob();
        })
        .then(function (blob) {
          return new Promise(function (resolve, reject) {
            var objectUrl = root.URL.createObjectURL(blob);
            var image = new root.Image();
            image.onload = function () {
              root.URL.revokeObjectURL(objectUrl);
              resolve(image);
            };
            image.onerror = function () {
              root.URL.revokeObjectURL(objectUrl);
              reject(new Error('image failed to decode: ' + src));
            };
            image.src = objectUrl;
          });
        });
    }

    function computePetGeometry(canvasWidth, canvasHeight, pet) {
      var widthPx = ((Number(pet.slot.width) || 0) * pet.scale * canvasWidth) / 100;
      return {
        widthPx: widthPx,
        // .pv-composite-pet is aspect-ratio: 1 - a square box sized by width,
        // not by the percentage-of-height that a literal height% would give.
        heightPx: widthPx,
        centerX: (clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) * canvasWidth) / 100,
        centerY: (clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) * canvasHeight) / 100,
        rotation: ((Number(pet.slot.rotation) || 0) * Math.PI) / 180,
      };
    }

    function drawCover(ctx, image, boxWidth, boxHeight) {
      var srcW = image.naturalWidth || image.width;
      var srcH = image.naturalHeight || image.height;
      if (!srcW || !srcH) return;
      var scale = Math.max(boxWidth / srcW, boxHeight / srcH);
      var drawW = srcW * scale;
      var drawH = srcH * scale;
      ctx.drawImage(image, -drawW / 2, -drawH / 2, drawW, drawH);
    }

    function drawPetLayer(ctx, geom, petImage, eraseMaskImage) {
      ctx.save();
      ctx.translate(geom.centerX, geom.centerY);
      ctx.rotate(geom.rotation);
      ctx.beginPath();
      ctx.rect(-geom.widthPx / 2, -geom.heightPx / 2, geom.widthPx, geom.heightPx);
      ctx.clip();
      if (eraseMaskImage) {
        // Same "keep" stencil the CSS mask uses (opaque = keep), baked via
        // destination-in instead of mask-image - see flattenErasedCutout in
        // pj-design-lab-controls.js for the same pattern on a single pet.
        var maskCanvas = doc.createElement('canvas');
        maskCanvas.width = geom.widthPx;
        maskCanvas.height = geom.heightPx;
        var maskCtx = maskCanvas.getContext('2d');
        var srcW = petImage.naturalWidth || petImage.width;
        var srcH = petImage.naturalHeight || petImage.height;
        var scale = Math.max(geom.widthPx / srcW, geom.heightPx / srcH);
        var drawW = srcW * scale;
        var drawH = srcH * scale;
        maskCtx.drawImage(
          petImage,
          (geom.widthPx - drawW) / 2,
          (geom.heightPx - drawH) / 2,
          drawW,
          drawH,
        );
        maskCtx.globalCompositeOperation = 'destination-in';
        maskCtx.drawImage(eraseMaskImage, 0, 0, geom.widthPx, geom.heightPx);
        ctx.drawImage(maskCanvas, -geom.widthPx / 2, -geom.heightPx / 2, geom.widthPx, geom.heightPx);
      } else {
        drawCover(ctx, petImage, geom.widthPx, geom.heightPx);
      }
      ctx.restore();
    }

    function drawAccessoryLayer(ctx, geom, accessoryImage, overlayPlacement) {
      var width = Number(overlayPlacement.width);
      var bottom = Number(overlayPlacement.bottom);
      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(bottom)) return;

      // Same lift formula as createAccessory(): distance from the pet's
      // centre to the accessory's bottom edge, as a % of the accessory's
      // own height.
      var lift = 50 + ((bottom - 50) / width) * 100;
      var accWidthPx = geom.widthPx * (width / 100);
      var accHeightPx = accWidthPx;

      ctx.save();
      ctx.translate(geom.centerX, geom.centerY);
      ctx.rotate(geom.rotation);
      // Lift is applied in the already-rotated local frame, same as the CSS
      // transform order (translate -50%/-50% then rotate then translateY) -
      // the accessory rides the pet's rotation rather than staying upright.
      ctx.translate(0, -(lift / 100) * accHeightPx);
      ctx.beginPath();
      ctx.rect(-accWidthPx / 2, -accHeightPx / 2, accWidthPx, accHeightPx);
      ctx.clip();
      drawCover(ctx, accessoryImage, accWidthPx, accHeightPx);
      ctx.restore();
    }

    // Pixel-space twin of appendArtboard(): the artboard is the pet's square
    // slot, accessory layers fill it whole, and the pet lands where
    // fitPetToArtboard() puts it.
    function drawArtboardLayers(ctx, geom, layer, petBox) {
      var size = geom.widthPx;
      var measured = measureImage(doc, layer.petImage);
      var fit = fitPetToArtboard(petBox, measured.width, measured.height, measured.bounds);
      var petX = (fit.left - 0.5) * size;
      var petY = (fit.top - 0.5) * size;
      var petW = fit.width * size;
      var petH = fit.height * size;

      ctx.save();
      ctx.translate(geom.centerX, geom.centerY);
      ctx.rotate(geom.rotation);
      if (layer.backAccessoryImage) {
        drawAdjustedAccessory(ctx, layer.backAccessoryImage, size, layerAdjust(layer.pet, true));
      }
      if (layer.eraseMaskImage) {
        var maskCanvas = doc.createElement('canvas');
        maskCanvas.width = Math.max(1, Math.round(petW));
        maskCanvas.height = Math.max(1, Math.round(petH));
        var maskCtx = maskCanvas.getContext('2d');
        maskCtx.drawImage(layer.petImage, 0, 0, maskCanvas.width, maskCanvas.height);
        maskCtx.globalCompositeOperation = 'destination-in';
        maskCtx.drawImage(layer.eraseMaskImage, 0, 0, maskCanvas.width, maskCanvas.height);
        ctx.drawImage(maskCanvas, petX, petY, petW, petH);
      } else {
        ctx.drawImage(layer.petImage, petX, petY, petW, petH);
      }
      if (layer.frontAccessoryImage) {
        drawAdjustedAccessory(ctx, layer.frontAccessoryImage, size, layerAdjust(layer.pet, false));
      }
      ctx.restore();
    }

    function currentComposition() {
      var placementApi = config.placement || root.PjDesignLabPlacement;
      var loadedPlacement = placementApi && placementApi.current && placementApi.current();
      var color = (config.resolveColor || resolveColor)(doc, context.color);
      var placement =
        loadedPlacement &&
        getSlots(placementApi, loadedPlacement.catalog, placementQuery(color));
      var composition = placement && buildComposition(placement, context);
      if (!composition || !composition.ok) return null;

      var flatLay =
        composition.imageUrl ||
        (config.resolveProductImage || resolveProductImage)(doc, composition.mediaId);
      if (!flatLay) return null;

      return {
        composition: composition,
        backgroundHex: placement.backgroundHex || '',
        flatLay: flatLay,
      };
    }

    function absoluteUrl(url) {
      if (!url) return null;
      try {
        return new root.URL(url, root.location && root.location.href).href;
      } catch (error) {
        return null;
      }
    }

    function renderedImageSize(src) {
      if (typeof doc.querySelectorAll !== 'function') return null;
      var images = doc.querySelectorAll('img');
      for (var index = 0; index < images.length; index += 1) {
        var image = images[index];
        if (
          image.getAttribute('src') !== src &&
          image.currentSrc !== src &&
          image.src !== src
        ) {
          continue;
        }
        if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
          return { width: image.naturalWidth, height: image.naturalHeight };
        }
      }
      return null;
    }

    function describeComposition() {
      if (typeof root.Promise === 'undefined') return null;

      var current = currentComposition();
      if (!current) return Promise.resolve(null);

      var rendered = renderedImageSize(current.flatLay);
      var frameSize = rendered
        ? Promise.resolve(rendered)
        : typeof root.Image === 'undefined'
          ? Promise.resolve(null)
          : loadCompositeImage(current.flatLay).then(function (baseImage) {
              return {
                width: baseImage.naturalWidth || baseImage.width,
                height: baseImage.naturalHeight || baseImage.height,
              };
            });

      return frameSize
        .then(function (size) {
          var width = size && size.width;
          var height = size && size.height;
          if (!(width > 0) || !(height > 0)) return null;
          return {
            frame: { width: width, height: height },
            color: current.composition.color,
            backgroundHex: current.backgroundHex,
            pets: current.composition.pets.map(function (pet) {
              var overlay = pet.accessoryOverlay;
              var placement = overlay && overlay.placement;
              var accessoryWidth = placement ? Number(placement.width) : NaN;
              var accessoryBottom = placement ? Number(placement.bottom) : NaN;
              var described = {
                slot: {
                  x: Number(pet.slot.x) || 0,
                  y: Number(pet.slot.y) || 0,
                  width: Number(pet.slot.width) || 0,
                  rotation: Number(pet.slot.rotation) || 0,
                },
                accessoryLayers:
                  Number.isFinite(accessoryWidth) && Number.isFinite(accessoryBottom)
                    ? {
                        front: absoluteUrl(overlay.asset),
                        back: absoluteUrl(overlay.backAsset),
                        placement: { width: accessoryWidth, bottom: accessoryBottom },
                      }
                    : null,
              };
              // Artboard accessories: what the preview actually drew. Production
              // must fit the pet into this box rather than use `placement`.
              if (overlay && overlay.petBox) {
                described.accessoryLayers = {
                  front: absoluteUrl(overlay.asset),
                  back: absoluteUrl(overlay.backAsset),
                  petBox: overlay.petBox,
                };
              }
              return described;
            }),
          };
        })
        .catch(function () {
          return null;
        });
    }

    function loadPetLayers(composition) {
      return Promise.all(composition.pets.map(function (pet) {
        var accessory = pet.accessoryOverlay;
        return Promise.all([
          loadCompositeImage(pet.imageUrl).catch(function () {
            return null;
          }),
          pet.eraseMaskUrl
            ? loadCompositeImage(pet.eraseMaskUrl).catch(function () {
                return null;
              })
            : Promise.resolve(null),
          accessory && accessory.backAsset
            ? loadCompositeImage(accessory.backAsset).catch(function () {
                return null;
              })
            : Promise.resolve(null),
          accessory && accessory.asset
            ? loadCompositeImage(accessory.asset).catch(function () {
                return null;
              })
            : Promise.resolve(null),
        ]).then(function (results) {
          return {
            pet: pet,
            petImage: results[0],
            eraseMaskImage: results[1],
            backAccessoryImage: results[2],
            frontAccessoryImage: results[3],
          };
        });
      }));
    }

    function drawPetWithAccessories(ctx, geom, layer) {
      var petBox = layer.pet.accessoryOverlay && layer.pet.accessoryOverlay.petBox;
      if (petBox) return drawArtboardLayers(ctx, geom, layer, petBox);
      var overlayPlacement = layer.pet.accessoryOverlay && layer.pet.accessoryOverlay.placement;
      if (layer.backAccessoryImage && overlayPlacement) {
        drawAccessoryLayer(ctx, geom, layer.backAccessoryImage, overlayPlacement);
      }
      drawPetLayer(ctx, geom, layer.petImage, layer.eraseMaskImage);
      if (layer.frontAccessoryImage && overlayPlacement) {
        drawAccessoryLayer(ctx, geom, layer.frontAccessoryImage, overlayPlacement);
      }
    }

    function uploadCompositeBlob(blob, filename) {
      var modal = doc.getElementById('pvModal');
      var endpoint = modal && modal.dataset && modal.dataset.uploadUrl;
      var apiToken = modal && modal.dataset && modal.dataset.apiToken;
      if (!blob || !endpoint || !apiToken || typeof root.fetch !== 'function') {
        return Promise.resolve(null);
      }
      var formData = new root.FormData();
      formData.append('image', blob, filename);
      return root
        .fetch(endpoint, { method: 'POST', headers: { 'api-token': apiToken }, body: formData })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (result) {
          if (!result || !result.imageUrl) return null;
          return /^https?:\/\//i.test(result.imageUrl)
            ? result.imageUrl
            : new root.URL(result.imageUrl, endpoint).toString();
        });
    }

    // Draws the flat-lay with every pet and accessory on it. Shared by the
    // upload export and the gallery preview so both are the same picture.
    function renderCompositeBlob() {
      if (
        typeof doc.createElement !== 'function' ||
        typeof root.Image === 'undefined' ||
        typeof root.Promise === 'undefined'
      ) {
        return Promise.resolve(null);
      }

      var current = currentComposition();
      if (!current) return Promise.resolve(null);
      var composition = current.composition;

      return loadCompositeImage(current.flatLay)
        .then(function (baseImage) {
          var canvas = doc.createElement('canvas');
          canvas.width = baseImage.naturalWidth || baseImage.width;
          canvas.height = baseImage.naturalHeight || baseImage.height;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);

          return loadPetLayers(composition).then(function (layers) {
            layers.forEach(function (layer) {
              // A pet whose own image failed to load draws nothing rather
              // than aborting the whole export - a partial thumbnail beats
              // none.
              if (!layer.petImage) return;
              drawPetWithAccessories(
                ctx,
                computePetGeometry(canvas.width, canvas.height, layer.pet),
                layer,
              );
            });

            return new Promise(function (resolve) {
              canvas.toBlob(function (blob) {
                resolve(
                  blob
                    ? {
                        blob: blob,
                        mediaId: composition.mediaId,
                        color: composition.colorKey,
                      }
                    : null,
                );
              }, 'image/png');
            });
          });
        })
        .catch(function () {
          return null;
        });
    }

    function exportComposite() {
      return renderCompositeBlob().then(function (rendered) {
        return rendered ? uploadCompositeBlob(rendered.blob, 'flat-lay-composite.png') : null;
      });
    }

    // The same composite as a local object URL for the PDP gallery. Never
    // uploaded, so the caller owns the URL and must revoke it.
    function previewComposite() {
      return renderCompositeBlob().then(function (rendered) {
        if (!rendered || !root.URL || typeof root.URL.createObjectURL !== 'function') {
          return null;
        }
        return {
          url: root.URL.createObjectURL(rendered.blob),
          mediaId: rendered.mediaId,
          color: rendered.color,
        };
      });
    }
    // One transparent PNG per pet - the pet (styled, erased) with its own
    // accessory layers, upright, on the pet's square artboard - so each pet's
    // finished artwork is available separately from the garment composite.
    var PET_FINAL_SIZE = 1024;

    function renderPetFinalBlob(layer) {
      var size = PET_FINAL_SIZE;
      var canvas = doc.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      var ctx = canvas.getContext('2d');
      var hasArtboard = layer.pet.accessoryOverlay && layer.pet.accessoryOverlay.petBox;
      // Artboard accessories fill the pet's own square; legacy ones sit above
      // the pet, so shrink and drop the pet to leave room for them.
      var petSize = hasArtboard ? size : size * 0.6;
      drawPetWithAccessories(
        ctx,
        {
          widthPx: petSize,
          heightPx: petSize,
          centerX: size / 2,
          centerY: hasArtboard ? size / 2 : size * 0.62,
          rotation: 0,
        },
        layer,
      );
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) {
          resolve(blob || null);
        }, 'image/png');
      });
    }

    function exportPetFinals() {
      if (
        typeof doc.createElement !== 'function' ||
        typeof root.Image === 'undefined' ||
        typeof root.Promise === 'undefined'
      ) {
        return Promise.resolve([]);
      }
      var current = currentComposition();
      if (!current) return Promise.resolve([]);

      return loadPetLayers(current.composition)
        .then(function (layers) {
          return Promise.all(
            layers.map(function (layer, index) {
              if (!layer.petImage) return null;
              return renderPetFinalBlob(layer)
                .then(function (blob) {
                  return uploadCompositeBlob(blob, 'pet-' + (index + 1) + '-final.png');
                })
                .catch(function () {
                  return null;
                });
            }),
          );
        })
        .catch(function () {
          return [];
        });
    }
    // --- End flat-lay composite export --------------------------------------

    function setContext(nextContext) {
      context = Object.assign({}, context, nextContext || {});
      return context;
    }

    function onLoadingComplete() {
      render();
    }

    function onLoadingFailed() {
      render();
    }

    function onVariantChange() {
      if (!context || !context.petCount) return;
      context.color = selectedBackground(doc);
      render();
    }

    function onZoomClick(event) {
      var target = event && event.target;
      if (!target) return;
      if (target.id === 'pvZoomIn') zoomIn();
      if (target.id === 'pvZoomOut') zoomOut();
    }

    function previewIsOpen() {
      var modal = doc.getElementById('pvModal');
      return Boolean(modal && modal.classList && modal.classList.contains('open'));
    }

    function onZoomKeydown(event) {
      if (!previewIsOpen() || isEditableTarget(event && event.target)) return;
      var delta = zoomDeltaFromKey(event);
      if (!delta) return;
      if (typeof event.preventDefault === 'function') event.preventDefault();
      setZoom(zoomScale + delta);
    }

    function init() {
      if (initialized || !doc || typeof doc.addEventListener !== 'function') return;
      initialized = true;
      doc.addEventListener('pj-design-lab:preview-loading-complete', onLoadingComplete);
      doc.addEventListener('pj-design-lab:preview-loading-failed', onLoadingFailed);
      doc.addEventListener('variant:change', onVariantChange);
      doc.addEventListener('change', onVariantChange);
      doc.addEventListener('click', onZoomClick);
      doc.addEventListener('keydown', onZoomKeydown);
      doc.addEventListener('contextmenu', onSaveAttempt);
      doc.addEventListener('dragstart', onSaveAttempt);
    }

    init();

    return {
      setContext: setContext,
      render: render,
      setZoom: setZoom,
      zoomIn: zoomIn,
      zoomOut: zoomOut,
      resetZoom: resetZoom,
      exportComposite: exportComposite,
      previewComposite: previewComposite,
      exportPetFinals: exportPetFinals,
      describeComposition: describeComposition,
      init: init,
    };
  }

  var controller = root.document ? createPreviewRenderer(root.document) : null;

  return {
    normalizeMediaId: normalizeMediaId,
    resolveProductImage: resolveProductImage,
    listGalleryMedia: listGalleryMedia,
    selectedBackground: selectedBackground,
    selectedStyle: selectedStyle,
    productColors: productColors,
    resolveColor: resolveColor,
    buildComposition: buildComposition,
    alphaBounds: alphaBounds,
    fitPetToArtboard: fitPetToArtboard,
    clampPercent: clampPercent,
    clampZoom: clampZoom,
    zoomDeltaFromKey: zoomDeltaFromKey,
    createPreviewRenderer: createPreviewRenderer,
    setContext: controller && controller.setContext,
    render: controller && controller.render,
    setZoom: controller && controller.setZoom,
    zoomIn: controller && controller.zoomIn,
    zoomOut: controller && controller.zoomOut,
    resetZoom: controller && controller.resetZoom,
    exportComposite: controller && controller.exportComposite,
    previewComposite: controller && controller.previewComposite,
    exportPetFinals: controller && controller.exportPetFinals,
    describeComposition: controller && controller.describeComposition,
    init: controller && controller.init,
  };
});
